import { Check, ChevronRight } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { Card, PageHeader } from "../../components/ui/patrones";
import { Aviso, Boton, Campo, Interruptor, Tabla } from "../../components/ui/primitivos";
import { cn } from "../../lib/cn";
import { aplicarAumento, fetchProductos, type SimulacionAumento } from "../../lib/consola-api";
import { useDatos } from "../../lib/useDatos";
import {
  type ConfiguracionDelAumento,
  confirmacionEsValida,
  cuerpoDelAumento,
  FRASE_DE_CONFIRMACION,
  porcentajeValido,
} from "./aumento";

/**
 * Aumento masivo de precios — `/precios/aumentar` (ADR 0070).
 *
 * **Es una pantalla con tres pasos y no un modal** (ADR 0064), como
 * `AumentarPreciosPage.tsx`: **Configurar → Auditar → Confirmar**. Es la
 * operación más destructiva de la consola —cierra TODOS los precios vigentes y
 * abre otros—, y el asistente existe para que nadie la dispare de un click.
 *
 * El modal tenía los dos porcentajes, la fecha de inicio y un botón
 * «Previsualizar». Faltaban el **filtro por producto** y el **hasta cuándo**
 * —que el endpoint acepta desde siempre y ninguna pantalla mandaba— y la
 * **confirmación escrita**.
 */
const PASOS = ["Configurar", "Auditar", "Confirmar"] as const;
type Paso = 0 | 1 | 2;

export function AumentoPagina() {
  const navegar = useNavigate();
  const productos = useDatos(() => fetchProductos());

  const [paso, setPaso] = useState<Paso>(0);
  const [config, setConfig] = useState<ConfiguracionDelAumento>({
    porcentajeNormal: "",
    porcentajeVip: "",
    vipAtadoAlNormal: true,
    vigenteDesde: new Date().toISOString().slice(0, 10),
    vigenteHasta: "",
    productoId: "",
  });
  const [simulacion, setSimulacion] = useState<SimulacionAumento | null>(null);
  const [confirmacion, setConfirmacion] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [trabajando, setTrabajando] = useState(false);

  const configuracionCompleta =
    porcentajeValido(config.porcentajeNormal) &&
    (config.vipAtadoAlNormal ||
      config.porcentajeVip === "" ||
      porcentajeValido(config.porcentajeVip)) &&
    config.vigenteDesde !== "";

  async function auditar() {
    setTrabajando(true);
    setError(null);
    try {
      setSimulacion(await aplicarAumento(cuerpoDelAumento(config), true));
      setPaso(1);
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo simular");
      setSimulacion(null);
    } finally {
      setTrabajando(false);
    }
  }

  async function aplicar() {
    setTrabajando(true);
    setError(null);
    try {
      await aplicarAumento(cuerpoDelAumento(config), false);
      navegar("/precios");
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo aplicar");
      setTrabajando(false);
    }
  }

  return (
    <div className="space-y-6">
      <nav aria-label="Miga de pan" className="flex items-center text-neutral-500 text-sm">
        <Link to="/precios" className="hover:text-neutral-900">
          Lista de Precios
        </Link>
        <ChevronRight className="mx-2 h-4 w-4" />
        <span className="font-medium text-neutral-900">Aumentar Precios</span>
      </nav>

      <PageHeader
        titulo="Aumentar Precios"
        subtitulo="Cierra los precios vigentes y abre los nuevos, sin dejar días sin precio"
        alVolver={() => navegar("/precios")}
      />

      <Pasos actual={paso} />

      {error ? <Aviso>{error}</Aviso> : null}

      {paso === 0 ? (
        <Card className="mx-auto max-w-3xl space-y-4 p-4">
          <h2 className="font-semibold text-neutral-900 text-sm">Configurar</h2>

          <div className="grid gap-3 sm:grid-cols-2">
            <Campo
              etiqueta="Porcentaje"
              type="number"
              value={config.porcentajeNormal}
              onChange={(e) => setConfig((c) => ({ ...c, porcentajeNormal: e.target.value }))}
              ayuda="15 sube 15%, -10 baja 10%"
            />
            <Campo
              etiqueta="Porcentaje VIP"
              type="number"
              value={config.vipAtadoAlNormal ? config.porcentajeNormal : config.porcentajeVip}
              disabled={config.vipAtadoAlNormal}
              onChange={(e) => setConfig((c) => ({ ...c, porcentajeVip: e.target.value }))}
            />
          </div>

          <div className="flex items-center gap-2 text-neutral-700 text-sm">
            <Interruptor
              aria-label="El precio VIP sube lo mismo"
              checked={config.vipAtadoAlNormal}
              onCheckedChange={(v) => setConfig((c) => ({ ...c, vipAtadoAlNormal: v }))}
            />
            <span aria-hidden="true">El precio VIP sube lo mismo que el normal</span>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Campo
              etiqueta="Los nuevos precios rigen desde"
              type="date"
              value={config.vigenteDesde}
              onChange={(e) => setConfig((c) => ({ ...c, vigenteDesde: e.target.value }))}
              ayuda="Los vigentes se cierran el día anterior"
            />
            <Campo
              etiqueta="Hasta (opcional)"
              type="date"
              value={config.vigenteHasta}
              onChange={(e) => setConfig((c) => ({ ...c, vigenteHasta: e.target.value }))}
              ayuda="Vacío = sin vencimiento"
            />
          </div>

          {/* El filtro que el endpoint acepta desde siempre y el modal no
              mandaba: sin él, todo aumento era sobre el catálogo entero. */}
          <div className="space-y-1">
            <label htmlFor="filtro-producto" className="block text-neutral-600 text-sm">
              Producto
            </label>
            <select
              id="filtro-producto"
              value={config.productoId}
              onChange={(e) => setConfig((c) => ({ ...c, productoId: e.target.value }))}
              className="w-full rounded-md border border-neutral-300 bg-white px-3 py-1.5 text-neutral-900 text-sm"
            >
              <option value="">Todos los productos</option>
              {(productos.datos ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre}
                </option>
              ))}
            </select>
          </div>

          <div className="flex justify-end gap-2">
            <Boton variante="secundario" onClick={() => navegar("/precios")}>
              Cancelar
            </Boton>
            <Boton disabled={trabajando || !configuracionCompleta} onClick={auditar}>
              {trabajando ? "Calculando..." : "Auditar"}
            </Boton>
          </div>
        </Card>
      ) : null}

      {paso === 1 && simulacion ? (
        <Card className="space-y-3 p-4">
          <h2 className="font-semibold text-neutral-900 text-sm">Auditar</h2>
          <Aviso tono="ok">
            {simulacion.afectados} precio(s) nuevos · {simulacion.aCerrar} se cierran el{" "}
            {simulacion.fechaCierre}
          </Aviso>

          <Tabla
            cabeceras={[
              { texto: "Antes", derecha: true },
              { texto: "Después", derecha: true },
              { texto: "VIP antes", derecha: true },
              { texto: "VIP después", derecha: true },
            ]}
          >
            {simulacion.muestra.map((m) => (
              <tr key={m.precioId}>
                <td className="px-4 py-1.5 text-right text-neutral-600 tabular-nums">{m.antes}</td>
                <td className="px-4 py-1.5 text-right font-medium tabular-nums">{m.despues}</td>
                <td className="px-4 py-1.5 text-right text-neutral-600 tabular-nums">
                  {m.vipAntes ?? "—"}
                </td>
                <td className="px-4 py-1.5 text-right font-medium tabular-nums">
                  {m.vipDespues ?? "—"}
                </td>
              </tr>
            ))}
          </Tabla>

          {/* La muestra son los primeros 20 que devuelve el worker. Decirlo
              importa: quien ve veinte filas y `afectados: 300` tiene que saber
              que no está mirando todo. */}
          {simulacion.afectados > simulacion.muestra.length ? (
            <p className="text-neutral-500 text-sm">
              Se muestran {simulacion.muestra.length} de {simulacion.afectados}.
            </p>
          ) : null}

          <div className="flex justify-end gap-2">
            <Boton variante="secundario" onClick={() => setPaso(0)}>
              Volver a configurar
            </Boton>
            <Boton onClick={() => setPaso(2)}>Continuar</Boton>
          </div>
        </Card>
      ) : null}

      {paso === 2 && simulacion ? (
        <Card className="mx-auto max-w-2xl space-y-3 border-red-200 bg-red-50 p-4">
          <h2 className="font-semibold text-red-800 text-sm">Confirmar</h2>
          <p className="text-red-900 text-sm">
            Se van a crear <strong>{simulacion.afectados}</strong> precios y cerrar{" "}
            <strong>{simulacion.aCerrar}</strong> el {simulacion.fechaCierre}. Esto afecta lo que se
            cotiza desde {config.vigenteDesde}.
          </p>
          <Campo
            etiqueta={`Escribí «${FRASE_DE_CONFIRMACION}» para aplicar`}
            value={confirmacion}
            onChange={(e) => setConfirmacion(e.target.value)}
            placeholder={FRASE_DE_CONFIRMACION}
          />
          <div className="flex justify-end gap-2">
            <Boton variante="secundario" onClick={() => setPaso(1)}>
              Volver
            </Boton>
            <Boton
              variante="peligro"
              disabled={trabajando || !confirmacionEsValida(confirmacion)}
              onClick={aplicar}
            >
              {trabajando ? "Aplicando..." : "Aplicar cambios"}
            </Boton>
          </div>
        </Card>
      ) : null}
    </div>
  );
}

/** El indicador de pasos de `WizardStepper`. */
function Pasos({ actual }: { actual: Paso }) {
  return (
    <ol className="mx-auto flex max-w-2xl items-center gap-2">
      {PASOS.map((nombre, i) => (
        <li key={nombre} className="flex flex-1 items-center gap-2">
          <span
            aria-current={i === actual ? "step" : undefined}
            className={cn(
              "flex h-7 w-7 shrink-0 items-center justify-center rounded-full font-semibold text-xs",
              i < actual && "bg-brand-600 text-white",
              i === actual && "bg-brand-600 text-white ring-4 ring-brand-100",
              i > actual && "bg-neutral-200 text-neutral-500",
            )}
          >
            {i < actual ? <Check className="h-4 w-4" /> : i + 1}
          </span>
          <span
            className={cn(
              "font-medium text-sm",
              i <= actual ? "text-neutral-900" : "text-neutral-500",
            )}
          >
            {nombre}
          </span>
          {i < PASOS.length - 1 ? (
            <span className={cn("h-px flex-1", i < actual ? "bg-brand-600" : "bg-neutral-200")} />
          ) : null}
        </li>
      ))}
    </ol>
  );
}
